/**
 * Email Campaigns Service
 * Handles all campaign-related operations for the admin dashboard
 */

import { supabase } from './supabase';

// =====================================================
// Types
// =====================================================

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  preview_text: string | null;
  template_type: 'wine_newsletter' | 'beer_newsletter' | 'general_newsletter' | 'event_invitation' | 'promotion' | 'educational_content';
  headline: string | null;
  body_content: string;
  cta_text: string | null;
  cta_url: string | null;
  image_url: string | null;
  audience_ids: string[];
  status: 'draft' | 'scheduled' | 'sent' | 'failed';
  scheduled_at: string | null;
  sent_at: string | null;
  recipient_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  error_message: string | null;
}

export interface CreateCampaignData {
  name: string;
  subject: string;
  preview_text?: string;
  template_type: Campaign['template_type'];
  headline?: string;
  body_content: string;
  cta_text?: string;
  cta_url?: string;
  image_url?: string;
  audience_ids: string[];
  status?: 'draft' | 'scheduled';
  scheduled_at?: string;
}

// Audience definitions
export const AUDIENCES = {
  wine_newsletter: {
    id: 'wine_newsletter',
    name: 'Wine Club Members',
    description: 'Wine club members and wine enthusiasts',
    envVar: 'RESEND_AUDIENCE_WINE_NEWSLETTER',
  },
  beer_newsletter: {
    id: 'beer_newsletter',
    name: 'Beer Club Members',
    description: 'Beer club members and beer enthusiasts',
    envVar: 'RESEND_AUDIENCE_BEER_NEWSLETTER',
  },
  general_newsletter: {
    id: 'general_newsletter',
    name: 'General Newsletter',
    description: 'All newsletter subscribers',
    envVar: 'RESEND_AUDIENCE_GENERAL_NEWSLETTER',
  },
  event_invitation: {
    id: 'event_invitation',
    name: 'Event Invitations',
    description: 'Customers interested in events',
    envVar: 'RESEND_AUDIENCE_EVENT_INVITATIONS',
  },
  promotion: {
    id: 'promotion',
    name: 'Promotions',
    description: 'Customers who want promotional offers',
    envVar: 'RESEND_AUDIENCE_PROMOTIONS',
  },
  educational_content: {
    id: 'educational_content',
    name: 'Educational Content',
    description: 'Wine/beer education subscribers',
    envVar: 'RESEND_AUDIENCE_EDUCATIONAL_CONTENT',
  },
};

// =====================================================
// Campaign CRUD Operations
// =====================================================

/**
 * Get all campaigns
 */
export const getCampaigns = async (): Promise<Campaign[]> => {
  try {
    const { data, error } = await supabase
      .from('email_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

/**
 * Get campaign by ID
 */
export const getCampaignById = async (id: string): Promise<Campaign | null> => {
  try {
    const { data, error } = await supabase
      .from('email_campaigns')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

/**
 * Create a new campaign
 */
export const createCampaign = async (
  campaignData: CreateCampaignData
): Promise<Campaign> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('email_campaigns')
      .insert({
        ...campaignData,
        created_by: user.id,
        status: campaignData.status || 'draft',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

/**
 * Update campaign
 */
export const updateCampaign = async (
  id: string,
  updates: Partial<CreateCampaignData>
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('email_campaigns')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

/**
 * Delete campaign
 */
export const deleteCampaign = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('email_campaigns')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

/**
 * Duplicate campaign
 */
export const duplicateCampaign = async (id: string): Promise<Campaign> => {
  try {
    const original = await getCampaignById(id);
    if (!original) throw new Error('Campaign not found');

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('email_campaigns')
      .insert({
        name: `${original.name} (Copy)`,
        subject: original.subject,
        preview_text: original.preview_text,
        template_type: original.template_type,
        headline: original.headline,
        body_content: original.body_content,
        cta_text: original.cta_text,
        cta_url: original.cta_url,
        image_url: original.image_url,
        audience_ids: original.audience_ids,
        status: 'draft',
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error duplicating campaign:', error);
    throw error;
  }
};

// =====================================================
// Campaign Sending
// =====================================================

/**
 * Send campaign immediately
 */
export const sendCampaign = async (id: string): Promise<void> => {
  try {
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      ? `${import.meta.env.VITE_NETLIFY_FUNCTIONS_URL}/send-email-campaign`
      : 'https://ejs-website-redesign.netlify.app/.netlify/functions/send-email-campaign';

    console.log('[Campaigns] Sending campaign to:', functionUrl);
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId: id }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.details || errorData.message || '';
      const errorMsg = errorData.error || 'Failed to send campaign';
      console.error('[Campaigns] Server error details:', errorData);
      throw new Error(detail ? `${errorMsg}: ${detail}` : errorMsg);
    }

    const result = await response.json();
    console.log('[Campaigns] Campaign sent:', result);
  } catch (error) {
    console.error('Error sending campaign:', error);
    throw error;
  }
};

/**
 * Send test email
 */
export const sendTestCampaign = async (
  id: string,
  testEmail: string
): Promise<void> => {
  try {
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      ? `${import.meta.env.VITE_NETLIFY_FUNCTIONS_URL}/send-test-campaign`
      : 'https://ejs-website-redesign.netlify.app/.netlify/functions/send-test-campaign';

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId: id, testEmail }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send test email');
    }

    const result = await response.json();
    console.log('[Campaigns] Test email sent:', result);
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};

/**
 * Schedule campaign for later
 */
export const scheduleCampaign = async (
  id: string,
  scheduledAt: string
): Promise<void> => {
  try {
    await updateCampaign(id, {
      status: 'scheduled',
      scheduled_at: scheduledAt,
    });
  } catch (error) {
    console.error('Error scheduling campaign:', error);
    throw error;
  }
};

/**
 * Get audience contact counts
 */
export const getAudienceCounts = async (): Promise<Record<string, number>> => {
  try {
    const functionUrl = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL
      ? `${import.meta.env.VITE_NETLIFY_FUNCTIONS_URL}/get-audience-counts`
      : 'https://ejs-website-redesign.netlify.app/.netlify/functions/get-audience-counts';

    console.log('[Campaigns] Fetching audience counts from:', functionUrl);

    const response = await fetch(functionUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to fetch audience counts');
    }

    const result = await response.json();
    console.log('[Campaigns] Audience counts received:', result.counts);
    return result.counts;
  } catch (error) {
    console.error('[Campaigns] Error fetching audience counts:', error);

    // For local development, return mock counts if function isn't available
    if (import.meta.env.DEV) {
      console.log('[Campaigns] Using mock counts for local development (matching real Resend data)');
      return {
        wine_newsletter: 53,
        beer_newsletter: 14,
        general_newsletter: 176,
        event_invitation: 164,
        promotion: 169,
        educational_content: 161,
      };
    }

    // Return empty counts on error in production
    return {};
  }
};
